/**
 * 租户切换组件
 * 用于在多租户环境下切换当前用户的工作租户
 *
 * 功能：
 * - 显示当前租户名称
 * - 下拉展示用户可访问的所有租户
 * - 点击切换到目标租户（会刷新Token）
 * - 支持移动端响应式布局
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Dropdown, Space, Typography, Avatar, Spin, Badge } from 'antd';
import { message } from '@/utils/antdStatic';
import type { MenuProps } from 'antd';
import {
  SwapOutlined,
  ShopOutlined,
  CheckOutlined,
  DownOutlined,
} from '@ant-design/icons';
import {
  getUserTenants,
  switchTenant,
  type UserTenantInfo,
} from '@/modules/foundation/api/tenantManagement';
import { STORAGE_KEYS, setStorageItem, getStorageItem } from '@/utils/storage';
import { clearPageTabs } from '@/layouts/TenantLayout/pageTabs/usePageTabs';
import './index.less';

const { Text } = Typography;

interface TenantSwitcherProps {
  /** 是否为紧凑模式（移动端） */
  compact?: boolean;
  /** 切换成功后的回调 */
  onSwitchSuccess?: (tenantId: number, tenantName: string) => void;
  /** 自定义类名 */
  className?: string;
}

/**
 * 租户切换组件
 */
const TenantSwitcher: React.FC<TenantSwitcherProps> = ({
  compact = false,
  onSwitchSuccess,
  className,
}) => {
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [tenants, setTenants] = useState<UserTenantInfo[]>([]);
  const [currentTenantId, setCurrentTenantId] = useState<number | null>(null);
  const [currentTenantName, setCurrentTenantName] = useState<string>('');

  // 从存储中获取当前租户信息
  useEffect(() => {
    const userInfo = getStorageItem(STORAGE_KEYS.USER_INFO);
    if (userInfo) {
      try {
        const parsed = typeof userInfo === 'string' ? JSON.parse(userInfo) : userInfo;
        setCurrentTenantId(parsed.tenant_id);
        setCurrentTenantName(parsed.tenant_name || '');
      } catch (e) {
        console.error('解析用户信息失败:', e);
      }
    }
  }, []);

  // 加载用户可访问的租户列表
  const loadTenants = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getUserTenants();
      if (result.success && result.data?.tenants) {
        setTenants(result.data.tenants);
      }
    } catch (error) {
      console.error('加载租户列表失败:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // 组件挂载时加载租户列表
  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  // 处理租户切换
  const handleSwitchTenant = useCallback(async (tenantId: number) => {
    if (tenantId === currentTenantId) {
      return; // 已经在当前租户
    }

    setSwitching(true);
    try {
      const result = await switchTenant({ tenant_id: tenantId });
      if (result.success && result.data) {
        const { tenantId: newTenantId, tenantName: newTenantName, tenantCode: newTenantCode } = result.data;

        // 更新 localStorage 中的用户信息（更新租户相关字段，保留其他字段）
        const storedRaw = getStorageItem(STORAGE_KEYS.USER_INFO);
        if (storedRaw) {
          const existing = typeof storedRaw === 'string' ? JSON.parse(storedRaw) : storedRaw;
          const updated = {
            ...existing,
            tenant_id: newTenantId,
            tenant_name: newTenantName,
            tenant_code: newTenantCode,
          };
          setStorageItem(STORAGE_KEYS.USER_INFO, JSON.stringify(updated));
        }

        // 更新当前状态
        setCurrentTenantId(newTenantId);
        setCurrentTenantName(newTenantName);

        message.success(`已切换到 ${newTenantName}`);

        // 触发回调
        onSwitchSuccess?.(newTenantId, newTenantName);

        // 刷新页面以重新加载数据，上一个租户打开的标签页不带过去
        clearPageTabs();
        setTimeout(() => {
          window.location.reload();
        }, 500);
      } else {
        message.error(result.message || '切换租户失败');
      }
    } catch (error) {
      console.error('切换租户失败:', error);
      message.error('切换租户失败，请重试');
    } finally {
      setSwitching(false);
    }
  }, [currentTenantId, onSwitchSuccess]);

  // 构建下拉菜单项
  const menuItems: MenuProps['items'] = tenants.filter((t) => t?.tenantId != null).map((tenant) => ({
    key: String(tenant.tenantId),
    label: (
      <div className="tenant-menu-item">
        <Space>
          <Avatar
            size="small"
            src={tenant.tenantLogo}
            icon={!tenant.tenantLogo && <ShopOutlined />}
            style={{ backgroundColor: tenant.tenantId === currentTenantId ? '#1890ff' : '#f5f5f5' }}
          />
          <div className="tenant-info">
            <Text
              strong={tenant.tenantId === currentTenantId}
              style={{ color: tenant.tenantId === currentTenantId ? '#1890ff' : undefined }}
            >
              {tenant.tenantName}
            </Text>
            {tenant.isPrimary === 1 && (
              <Badge
                count="主"
                style={{
                  backgroundColor: '#52c41a',
                  fontSize: 10,
                  marginLeft: 4,
                }}
              />
            )}
          </div>
        </Space>
        {tenant.tenantId === currentTenantId && (
          <CheckOutlined style={{ color: '#1890ff' }} />
        )}
      </div>
    ),
    onClick: () => handleSwitchTenant(tenant.tenantId),
    disabled: switching,
  }));

  // 如果只有一个租户，不显示切换器
  if (tenants.length <= 1 && !loading) {
    return null;
  }

  // 紧凑模式（移动端）
  if (compact) {
    return (
      <Dropdown
        menu={{ items: menuItems }}
        trigger={['click']}
        placement="bottomRight"
        disabled={switching}
      >
        <div className={`tenant-switcher-compact ${className || ''}`}>
          {switching ? (
            <Spin size="small" />
          ) : (
            <SwapOutlined style={{ fontSize: 18 }} />
          )}
        </div>
      </Dropdown>
    );
  }

  // 正常模式
  return (
    <Dropdown
      menu={{ items: menuItems }}
      trigger={['click']}
      placement="bottomRight"
      disabled={switching}
    >
      <div className={`tenant-switcher ${className || ''}`}>
        <Space>
          <Avatar
            size="small"
            icon={<ShopOutlined />}
            style={{ backgroundColor: '#1890ff' }}
          />
          <Text className="tenant-name" ellipsis style={{ maxWidth: 120 }}>
            {currentTenantName || '选择租户'}
          </Text>
          {switching ? <Spin size="small" /> : <DownOutlined style={{ fontSize: 10 }} />}
        </Space>
      </div>
    </Dropdown>
  );
};

export default TenantSwitcher;
